import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { type CarouselComposeLogEntry, type CarouselItem, type CarouselLlmEvent } from "@/lib/api";
import { copyText } from "@/lib/carouselGenerationPack";
import { estimateRenderProgress, etaLabel } from "@/lib/renderProgress";
import type { AiJobKind, AiJobParam } from "@/components/AiJobModal";
import LlmReceiveProgress from "@/components/LlmReceiveProgress";
import LlmJobDebug from "@/components/LlmJobDebug";
import { Button } from "@/components/ui/button";
import { OP_SURFACE } from "@/lib/opSurfaces";
import { cn } from "@/lib/utils";

const LOG_KIND_LABEL: Record<string, string> = {
  start: "start",
  score_now: "score teraz",
  score_after: "score po",
  prompt_analyze: "prompt analizy",
  prompt_verify: "prompt weryfikacji",
  response: "odpowiedź",
  ops: "operacje",
  verify: "weryfikacja",
  stop: "stop",
  call: "wysyłam",
  first_token: "leci tekst",
  alive: "czekam na znak",
  prompt_ready: "prompt gotowy",
};

type JobLogRow = {
  at: string;
  kind: string;
  turn?: number;
  text?: string;
  prompt?: string;
  raw?: string;
  ops?: string[];
  model?: string;
};

function clockOf(at?: string): string {
  if (!at) return "";
  const hit = at.match(/T(\d{2}:\d{2}:\d{2})/);
  return hit?.[1] || at.slice(11, 19);
}

function fromLoopEntry(entry: CarouselComposeLogEntry): JobLogRow {
  return {
    at: entry.at || "",
    kind: entry.kind || "log",
    turn: entry.turn,
    text: entry.text,
    prompt: entry.prompt,
    raw: entry.raw,
    ops: entry.ops,
    model: entry.model,
  };
}

function fromLlmEvent(event: CarouselLlmEvent): JobLogRow {
  const bits = [event.detail, event.chars != null ? `${event.chars} zn` : ""].filter(Boolean);
  return {
    at: event.at || "",
    kind: event.event || "event",
    text: bits.join(" · "),
    model: event.model,
  };
}

function formatLogLine(row: JobLogRow): string {
  const when = clockOf(row.at);
  const kind = LOG_KIND_LABEL[row.kind] || row.kind;
  const turn = row.turn ? `tura ${row.turn}` : "";
  const head = [when, turn, kind].filter(Boolean).join("  ");
  const chunks = [head];
  if (row.model) chunks.push(row.model);
  if (row.ops?.length) chunks.push(row.ops.join(", "));
  if (row.text) chunks.push(row.text);
  if (row.prompt) chunks.push(`PROMPT\n${row.prompt}`);
  if (row.raw) chunks.push(`ODPOWIEDŹ\n${row.raw}`);
  return chunks.join(" · ");
}

function collectJobLog(item?: CarouselItem | null): JobLogRow[] {
  const rows: JobLogRow[] = [];
  for (const entry of item?.design_review?.loop?.log || []) {
    rows.push(fromLoopEntry(entry));
  }
  for (const event of item?.llm_progress?.events || []) {
    rows.push(fromLlmEvent(event));
  }
  rows.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  return rows;
}

const JOB_TITLE: Partial<Record<AiJobKind, string>> = {
  render: "Skład obrazków",
  houseFix: "Poprawa serii",
  designReview: "Ocena designu",
  designApply: "Wdrażanie designu",
  layoutQa: "QA składu",
};

function isLlmRenderJob(kind: AiJobKind | null | undefined): boolean {
  return kind === "houseFix" || kind === "designReview" || kind === "designApply" || kind === "layoutQa";
}

export default function RenderJobInline({
  kind,
  item,
  params,
  running,
  failed,
  statusLabel,
  error,
  abortPending,
  onAbort,
  className,
}: {
  kind?: AiJobKind | null;
  item?: CarouselItem | null;
  params?: AiJobParam[];
  running: boolean;
  failed?: boolean;
  statusLabel?: string;
  error?: string;
  abortPending?: boolean;
  onAbort?: () => void;
  className?: string;
}) {
  const total = item?.render_total || 0;
  const done = item?.render_done || 0;
  const status = item?.render_status || "";
  const llmMode = isLlmRenderJob(kind);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);
  const logScroller = useRef<HTMLDivElement | null>(null);
  const rows = useMemo(() => collectJobLog(item), [item]);

  useEffect(() => {
    if (!running || llmMode) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [running, llmMode]);

  useEffect(() => {
    const node = logScroller.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [rows.length, item?.llm_detail]);

  const live = estimateRenderProgress(
    item?.render_progress,
    { done, total, phase: status === "done" ? "done" : status || "queued" },
    nowTick,
  );
  const percent = running && !llmMode ? live.percent : total > 0 ? Math.round((done / Math.max(total, 1)) * 100) : 0;
  const title = JOB_TITLE[kind || "render"] || "Job na Wyjściu";
  const show = Boolean(
    running ||
      failed ||
      error ||
      status === "design" ||
      item?.llm_status === "design_review" ||
      (kind && aiJobVisible(kind, item, running)),
  );

  if (!show) return null;

  const copyLog = async () => {
    const detail = (item?.llm_detail || "").trim();
    const lines = rows.map(formatLogLine);
    if (detail && !lines.some((line) => line.includes(detail))) lines.push(detail);
    const ok = await copyText(lines.join("\n\n") || "brak wpisów");
    setCopied(ok);
    window.setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div
        className={cn(
          "sticky top-0 z-20 space-y-2 p-3 backdrop-blur",
          failed ? OP_SURFACE.danger.box : running ? OP_SURFACE.process.box : OP_SURFACE.work.box,
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold text-slate-900">
              {running ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin text-cyan-700" /> : null}
              {title}
            </p>
            <p className={cn("text-sm", failed ? "text-destructive" : "text-slate-800")}>
              {statusLabel || (running ? "W toku…" : failed ? "Błąd" : "Gotowe")}
            </p>
          </div>
          {running && onAbort ? (
            <Button type="button" size="sm" variant="outline" disabled={abortPending} onClick={onAbort}>
              Przerwij
            </Button>
          ) : null}
        </div>

        {!llmMode ? (
          <div>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span>
                {done} / {total || "?"} slajdów
              </span>
              <span className="font-medium tabular-nums">{percent}%</span>
            </div>
            <div className="mb-1 h-2 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-cyan-600 transition-all" style={{ width: `${percent}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">
              {running
                ? `minęło ${etaLabel(live.elapsed_sec)} · zostało ok. ${etaLabel(live.eta_sec)}`
                : status === "done"
                  ? "Gotowe."
                  : status === "failed"
                    ? "Przerwane lub błąd."
                    : "Czekam."}
            </p>
          </div>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {item?.render_error && status === "failed" ? (
          <p className="text-sm text-destructive">{item.render_error}</p>
        ) : null}

        {llmMode || item?.llm_status === "design_review" || status === "design" ? (
          <LlmReceiveProgress
            className="mt-1"
            progress={item?.llm_progress}
            llmStatus={item?.llm_status}
            fallbackStartedAt={item?.llm_progress?.started_at}
          />
        ) : null}
      </div>

      {params?.length ? (
        <details className={cn("p-3 text-xs", OP_SURFACE.optional.box)}>
          <summary className="cursor-pointer font-medium text-violet-950">Parametry joba</summary>
          <dl className="mt-2 grid gap-1 sm:grid-cols-2">
            {params.map((row) => (
              <div key={row.label} className="flex gap-2">
                <dt className="shrink-0 text-violet-800/80">{row.label}:</dt>
                <dd className="min-w-0 truncate font-medium text-violet-950">{row.value}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}

      <details className={cn("p-3", OP_SURFACE.log.box)}>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-medium text-zinc-100">
          <span>Log / prompt</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-zinc-500 text-zinc-100 hover:bg-zinc-700"
            onClick={(e) => {
              e.preventDefault();
              void copyLog();
            }}
          >
            <Copy className="mr-2 h-3.5 w-3.5" />
            {copied ? "Skopiowano" : "Kopiuj log"}
          </Button>
        </summary>
        <div
          ref={logScroller}
          className="mt-2 max-h-48 overflow-auto rounded-none border border-zinc-700 bg-zinc-950/80 p-2 text-[11px] leading-5 text-zinc-200"
        >
          {rows.length ? (
            rows.map((row, i) => (
              <p key={`${row.at}-${row.kind}-${i}`} className="whitespace-pre-wrap border-b border-zinc-800/80 py-1 last:border-0">
                {formatLogLine(row)}
              </p>
            ))
          ) : (
            <p className="text-zinc-400">
              {running ? "Log pojawi się po pierwszym zdarzeniu." : "Brak wpisów w tym przebiegu."}
            </p>
          )}
          {(item?.llm_detail || "").trim() ? (
            <p className="mt-2 whitespace-pre-wrap border-t border-zinc-700 pt-2 text-zinc-300">{item?.llm_detail}</p>
          ) : null}
        </div>
        {item?.llm_progress ? (
          <LlmJobDebug className="mt-2" item={item} progress={item.llm_progress} />
        ) : null}
      </details>
    </div>
  );
}

function aiJobVisible(kind: AiJobKind, item?: CarouselItem | null, running?: boolean): boolean {
  if (running) return true;
  if (kind === "render") {
    const st = item?.render_status || "";
    return st === "queued" || st === "rendering" || st === "design" || st === "failed";
  }
  const llm = item?.llm_status || "";
  return llm === "running" || llm === "queued" || llm === "design_review" || llm === "error";
}
